import { stairObstructions } from './stairs.js';
import { RAD, WALL_THICKNESS } from './constants.js';
import { obstacleParts, presentInScene } from './sceneObjects.js';
import { ceilingPrisms } from './buildingEnvelope.js';
import { wallJointPrisms } from './wallJoints.js';

const jointCache = new WeakMap();

/** Compile walls, apertures, door leaves and box objects into common prisms.
 * The engine, walking controls and renderer all consume this representation. */
export function sceneWalls(S, state = S.sceneState || 'design') {
  if (S._sceneWalls) return S._sceneWalls;
  const out = [...ceilingPrisms(S)];

  for (const wall of S.walls) {
    const length = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
    if (length < 1e-6) continue;
    const ux = (wall.x2 - wall.x1) / length, uy = (wall.y2 - wall.y1) / length;
    const segment = (a, b, bottom = 0, top = wall.h, extra = {}) => {
      if (b - a < 1e-6 || top <= bottom) return;
      out.push({ ...wall, ...extra, sourceId: wall.id, x1: wall.x1 + ux * a, y1: wall.y1 + uy * a,
        x2: wall.x1 + ux * b, y2: wall.y1 + uy * b, bottom, h: top });
    };
    const openings = (S.openings || []).filter(o => o.wallId === wall.id).map(o => ({ ...o,
      start: Math.max(0, o.along - o.width / 2), end: Math.min(length, o.along + o.width / 2)
    })).filter(o => o.end > o.start).sort((a, b) => a.start - b.start);
    let end = 0;
    for (const o of openings) {
      // Overlapping openings are flagged by validation/UI; clipping here keeps
      // malformed older plans from duplicating a full wall over an aperture.
      const start = Math.max(end, o.start);
      if (o.end <= start) continue;
      segment(end, start);
      const bottom = o.kind === 'window' ? (o.bottom ?? 0.9) : 0;
      segment(start, o.end, 0, bottom);
      segment(start, o.end, bottom + o.height, wall.h);
      if (o.kind === 'window') segment(start, o.end, bottom, Math.min(wall.h, bottom + o.height), { kind: 'glass', mountRule: 'prohibited', openingId: o.id });
      if (o.kind === 'door') {
        const angle = state === 'closed' ? 0 : state === 'open' ? 90 : (o.openAngle ?? 90);
        const x1 = wall.x1 + ux * start, y1 = wall.y1 + uy * start;
        const a = Math.atan2(uy, ux) + angle * RAD, width = o.end - start;
        out.push({ ...wall, id: `leaf-${o.id}`, sourceId: o.id, openingId: o.id, name: o.name,
          kind: o.material === 'glass' ? 'glass' : 'solid', mountRule: 'prohibited', thickness: 0.04,
          x1, y1, x2: x1 + Math.cos(a) * width, y2: y1 + Math.sin(a) * width,
          bottom: 0, h: Math.min(wall.h, o.height), objectKind: 'door' });
      }
      end = o.end;
    }
    segment(end, length);
  }
  const jointKey = JSON.stringify([S.walls, S.openings, state]);
  let joints = jointCache.get(S.walls);
  if (joints?.key !== jointKey) {
    joints = { key: jointKey, value: wallJointPrisms(out) }; jointCache.set(S.walls, joints);
  }
  out.push(...joints.value);
  for (const o of S.obstacles || []) {
    if (!presentInScene(o, state)) continue;
    const a = (o.pan || 0) * RAD, ux = Math.cos(a), uy = Math.sin(a);
    for (const [i, part] of obstacleParts(o).entries()) {
      const x = o.x + ux * part.x - uy * part.y, y = o.y + uy * part.x + ux * part.y;
      out.push({ id: `${o.id}:${i}`, sourceId: o.id, name: o.name, objectKind: o.kind, part: part.role, color: part.color, kind: o.transparent ? 'glass' : 'solid',
        x1: x - ux * part.width / 2, y1: y - uy * part.width / 2,
        x2: x + ux * part.width / 2, y2: y + uy * part.width / 2,
        bottom: (o.bottom || 0) + part.z - part.height / 2, h: (o.bottom || 0) + part.z + part.height / 2,
        thickness: part.depth || WALL_THICKNESS, mountRule: 'prohibited' });
    }
  }
  out.push(...stairObstructions(S));
  return out;
}

export function openingCentre(S, o) {
  const w = S.walls.find(w => w.id === o.wallId);
  if (!w) return null;
  const length = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
  if (!length) return null;
  const ux = (w.x2 - w.x1) / length, uy = (w.y2 - w.y1) / length;
  return { x: w.x1 + ux * o.along, y: w.y1 + uy * o.along, ux, uy, heading: (Math.atan2(ux, -uy) / RAD + 360) % 360 };
}

export function sceneIssues(S) {
  const issues = [];
  for (const o of S.openings || []) {
    const w = S.walls.find(w => w.id === o.wallId);
    if (!w) { issues.push(`${o.name}: associated wall is missing.`); continue; }
    const length = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
    if (o.along - o.width / 2 < -0.01 || o.along + o.width / 2 > length + 0.01) issues.push(`${o.name}: opening extends beyond its wall.`);
    if ((o.kind === 'window' ? o.bottom ?? 0.9 : 0) + o.height > w.h + 0.01) issues.push(`${o.name}: opening height exceeds its wall.`);
    if ((S.openings || []).some(p => p.id !== o.id && p.wallId === o.wallId && Math.abs(p.along - o.along) < (p.width + o.width) / 2 - 0.01)) issues.push(`${o.name}: overlapping openings need correction.`);
  }
  for (const t of S.tasks || []) if (t.enabled !== false && (t.minDuration || 0) > (t.length ?? 1) / Math.max(0.01, t.speed ?? 1.4) + 1e-9) issues.push(`${t.name}: requested capture duration exceeds the time spent on the path.`);
  return issues;
}
